<template>
  <el-dropdown trigger="click" :disabled="state.deleting" @command="onCommand">
    <el-button class="ack-document-actions__trigger" text :loading="state.deleting">
      <el-icon :size="18"><MoreFilled class="ack-document-actions__icon" /></el-icon>
    </el-button>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item command="delete" :icon="Delete">
          {{ t('hubstr.ack_documents.table.delete') }}
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script setup lang="ts">
import {
  ComponentInternalInstance, getCurrentInstance, PropType, reactive,
} from 'vue';
import { Delete, MoreFilled } from '@element-plus/icons-vue';
import { translate, useTranslate } from 'magner';
import { ElMessage, ElMessageBox } from 'magner/element-plus';
import { ackDocumentDelete, logAckInfo } from 'features/ack-documents/requests';
import { AckDocument } from 'features/ack-documents/types';
import { isConfirmCancelled } from 'features/ack-documents/utils/translate';

const props = defineProps({
  row: {
    type: Object as PropType<AckDocument>,
    required: true,
  },
  column: {
    type: Object as PropType<unknown>,
    default: null,
  },
});

const { customT } = useTranslate();
const t = (code: string) => customT(translate(code));

const state = reactive({ deleting: false });

const instance = getCurrentInstance();

/**
 * Кастомная ячейка Magner не может отправить 'update-table': события из неё таблица не слушает.
 * Поэтому ищем родительскую страницу TablePage и вызываем её filtersAction — тот же путь,
 * которым Magner обрабатывает emits: 'update-table' у кнопок.
 */
const refreshTable = async () => {
  let parent: ComponentInternalInstance | null = instance?.parent ?? null;
  while (parent && parent.type.name !== 'TablePage') {
    parent = parent.parent;
  }

  const filtersAction = (parent?.proxy as { filtersAction?: (action: { emits: string }) => Promise<void> } | null)
    ?.filtersAction;
  if (!filtersAction) {
    console.warn('[AckDocuments.delete] TablePage not found, reloading page');
    window.location.reload();
    return;
  }

  await filtersAction({ emits: 'update-table' });
};

const removeDocument = async () => {
  logAckInfo('delete', 'click', { id: props.row.id });

  try {
    await ElMessageBox.confirm(
      t('hubstr.ack_documents.table.delete_confirm_text'),
      t('hubstr.ack_documents.table.delete_confirm_title'),
      {
        confirmButtonText: t('hubstr.ack_documents.table.delete'),
        cancelButtonText: t('hubstr.ack_documents.table.delete_cancel'),
        type: 'warning',
      },
    );
  } catch (e) {
    if (!isConfirmCancelled(e)) {
      console.error('[AckDocuments.delete] confirm failed', e);
    }
    logAckInfo('delete', 'cancelled', { id: props.row.id });
    return;
  }

  state.deleting = true;
  try {
    const res = await ackDocumentDelete(props.row.id);
    if (res.status === 404) {
      ElMessage.warning(t('hubstr.ack_documents.table.delete_not_found'));
    } else if (res.error) {
      ElMessage.error(res.error);
      return;
    } else {
      ElMessage.success(t('hubstr.ack_documents.table.delete_success'));
    }
    await refreshTable();
  } catch (e) {
    console.error('[AckDocuments.delete] unexpected error', e);
    ElMessage.error(t('hubstr.ack_documents.errors.common'));
  } finally {
    state.deleting = false;
  }
};

const onCommand = (command: string) => {
  if (command === 'delete') {
    removeDocument();
  }
};
</script>
